import { CheckSquare, Hourglass, Inbox } from "lucide-react";
import {
  RecordContextProvider,
  useGetList,
  useGetRecordRepresentation,
  useLocaleState,
  useTranslate,
} from "ra-core";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { firstOpenStage } from "../deals/stages";
import { CountUp } from "../misc/CountUp";
import { formatCompactCurrency } from "../misc/formatCurrency";
import { ALL_PROJECTS, useCurrentProject } from "../projects/useCurrentProject";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { AddTask } from "../tasks/AddTask";
import type { Lead } from "../types";
import { TasksListByDueDate } from "../tasks/TasksListByDueDate";
import { useTasksByDueDate } from "../tasks/useTasksByDueDate";
import { useStaleDeals } from "./useStaleDeals";

const MAX_STALE_DEALS = 10;

/** position in the cascade: cards rise one after another (theme «Графит») */
const cascade = (index: number) => ({ "--vv-i": index }) as CSSProperties;

/**
 * «Сегодня», the home page: three counters, my tasks for today and the
 * overdue ones, new leads and deals without movement
 * for 7 days. Nothing else: the Atomic widgets (charts, activity log, hot
 * contacts) went.
 */
export const TodayContent = () => {
  const translate = useTranslate();
  const [locale = "ru"] = useLocaleState();
  const today = new Date().toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="flex flex-col gap-6 md:gap-8 mt-1">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {translate("crm.today.title")}
        </h1>
        <p className="text-sm text-muted-foreground first-letter:uppercase">
          {today}
        </p>
      </div>
      <Counters />
      <NewLeads />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <TodayTasks />
        <StaleDeals />
      </div>
    </div>
  );
};

const Block = ({
  icon,
  title,
  action,
  index = 0,
  children,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
  index?: number;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-3 min-w-0">
    <div className="flex items-center gap-2 min-h-8">
      <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>
      <h2 className="vv-heading flex-1">{title}</h2>
      {action}
    </div>
    <Card className="p-5 vv-rise" style={cascade(index + 3)}>
      {children}
    </Card>
  </section>
);

/**
 * Three numbers at the top, as on the dashboard: overdue tasks, today's
 * tasks, idle deals. The bad ones glow red while they are not zero.
 */
const Counters = () => {
  const translate = useTranslate();
  const { tasksBySection } = useTasksByDueDate();
  const { staleDeals } = useStaleDeals();
  return (
    <div className="grid grid-cols-3 gap-3 md:gap-4">
      <Counter
        index={0}
        label={translate("crm.today.counter_overdue")}
        value={tasksBySection.overdue.length}
        tone="bad"
      />
      <Counter
        index={1}
        label={translate("crm.today.counter_today")}
        value={tasksBySection.today.length}
        tone="accent"
      />
      <Counter
        index={2}
        label={translate("crm.today.counter_stale")}
        value={staleDeals.length}
        tone="bad"
      />
    </div>
  );
};

const Counter = ({
  label,
  value,
  tone,
  index,
}: {
  label: string;
  value: number;
  tone: "accent" | "bad";
  index: number;
}) => {
  const alert = tone === "bad" && value > 0;
  return (
    <div
      role="group"
      aria-label={label}
      data-alert={alert}
      style={cascade(index)}
      className={cn(
        "vv-rise vv-card-hover rounded-lg border bg-card text-card-foreground p-3 md:p-5 min-w-0",
        alert && "vv-pulse border-destructive/40",
      )}
    >
      <CountUp
        value={value}
        label={label}
        className={cn(
          "block text-2xl md:text-[2rem] leading-none font-bold tabular-nums mb-1.5",
          tone === "accent"
            ? "text-primary-ink"
            : alert
              ? "text-destructive"
              : "text-foreground",
        )}
      />
      <span
        aria-hidden
        className="vv-heading block text-[0.625rem] tracking-[0.06em] leading-tight md:text-[0.6875rem] md:tracking-[0.14em]"
      >
        {label}
      </span>
    </div>
  );
};

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="text-sm text-muted-foreground">{children}</p>
);

const TodayTasks = () => {
  const translate = useTranslate();
  return (
    <Block
      icon={<CheckSquare />}
      index={0}
      title={translate("crm.today.my_tasks")}
      action={<AddTask display="icon" selectContact />}
    >
      <TasksListByDueDate
        sections={["overdue", "today"]}
        emptyPlaceholder={<Empty>{translate("crm.today.no_tasks")}</Empty>}
      />
    </Block>
  );
};

/**
 * «Новые лиды»: the leads on the first open stage of the current project's
 * lead pipeline, the same column the lead board starts with (with «Все
 * проекты»: that stage of every project). The newest five, and how many in all.
 */
const NewLeads = () => {
  const translate = useTranslate();
  const represent = useGetRecordRepresentation("leads");
  const { currentId, projects, pipelinesOf, stagesOf, isPending } =
    useCurrentProject();
  const projectIds =
    currentId === ALL_PROJECTS
      ? projects.map((p) => p.id)
      : currentId != null
        ? [currentId]
        : [];
  const stageIds = projectIds
    .map((pid) => {
      const stages = stagesOf(pipelinesOf(pid, "lead")[0]?.id);
      return stages.length ? firstOpenStage(stages).id : null;
    })
    .filter((id) => id != null);
  const { data: leads, total } = useGetList<Lead>(
    "leads",
    {
      pagination: { page: 1, perPage: 5 },
      sort: { field: "created_at", order: "DESC" },
      filter: { "stage_id@in": `(${stageIds.join(",")})` },
    },
    { enabled: !isPending && stageIds.length > 0 },
  );
  if (!leads?.length) return null;
  return (
    <Block
      icon={<Inbox />}
      title={translate("crm.today.new_leads")}
      action={
        <Link
          to="/leads"
          className="text-sm font-semibold tabular-nums text-primary-ink"
          aria-label={translate("crm.leads.column_count", {
            smart_count: total ?? leads.length,
          })}
        >
          {total ?? leads.length}
        </Link>
      }
    >
      <ul className="flex flex-col divide-y -my-2">
        {leads.map((lead) => (
          <li key={lead.id}>
            <Link
              to={`/leads/${lead.id}/show`}
              className="flex items-baseline justify-between gap-3 py-2 hover:bg-muted -mx-2 px-2 rounded-md"
            >
              <span className="text-sm font-medium min-w-0 truncate">
                {represent(lead)}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {translate(`crm.leads.sources.${lead.source}`)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {total != null && total > leads.length ? (
        <Link
          to="/leads"
          className="mt-3 block text-sm text-muted-foreground underline"
        >
          {translate("crm.today.more", { smart_count: total - leads.length })}
        </Link>
      ) : null}
    </Block>
  );
};

const StaleDeals = () => {
  const translate = useTranslate();
  const { currency } = useConfigurationContext();
  const { staleDeals, isPending } = useStaleDeals();
  const shown = staleDeals.slice(0, MAX_STALE_DEALS);

  return (
    <Block
      icon={<Hourglass />}
      index={1}
      title={translate("crm.today.stale_deals")}
    >
      {isPending ? null : !shown.length ? (
        <Empty>{translate("crm.today.no_stale_deals")}</Empty>
      ) : (
        <>
          <ul className="flex flex-col divide-y -my-2">
            {shown.map(({ deal, idleDays }) => (
              <li key={deal.id}>
                <RecordContextProvider value={deal}>
                  <Link
                    to={`/deals/${deal.id}/show`}
                    className="flex items-start justify-between gap-3 py-2 hover:bg-muted -mx-2 px-2 rounded-md"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {deal.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {deal.company_id != null ? (
                          <>
                            <ReferenceField
                              source="company_id"
                              reference="companies"
                              link={false}
                            />
                            {" · "}
                          </>
                        ) : null}
                        {deal.stage}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums text-primary-ink">
                        {formatCompactCurrency(deal.amount ?? 0, currency)}
                      </p>
                      <p className="mt-1 inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[0.6875rem] font-semibold bg-destructive/10 text-destructive">
                        <span
                          className="size-1.5 rounded-full bg-destructive"
                          aria-hidden
                        />
                        {translate("crm.today.idle_days", {
                          smart_count: idleDays,
                        })}
                      </p>
                    </div>
                  </Link>
                </RecordContextProvider>
              </li>
            ))}
          </ul>
          {staleDeals.length > shown.length ? (
            <Link
              to="/deals"
              className="mt-3 block text-sm text-muted-foreground underline"
            >
              {translate("crm.today.more", {
                smart_count: staleDeals.length - shown.length,
              })}
            </Link>
          ) : null}
        </>
      )}
    </Block>
  );
};
